import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { isFuture } from 'date-fns';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconSignalStream } from '@sorare/core/src/atoms/icons/system/iconSignalStream.ts';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import type {
  ClickAddToCart_Origin,
  ClickInstantBuy_Flow,
} from '@sorare/core/src/protos/events/so5/shared/events';

import { PriceLockedRibbon } from '#src/components/primaryBuy/PriceLockedRibbon/index.tsx';
import { PrimaryOfferStockCounter } from '#src/components/primaryBuy/PrimaryOfferStockCounter/index.tsx';
import { TokenImg } from '#src/components/token/Token/TokenImg/index.tsx';
import { useGetAuctionDetails } from '#src/hooks/offers/useGetAuctionDetails.ts';

import { Actions } from './Actions/index.tsx';
import { CornerBadges } from './CornerBadges/index.tsx';
import { Details } from './Details/index.tsx';
import { MarketCreditCornerBadge } from './MarketCreditCornerBadge/index.tsx';
import type { PlayerDrawerToken_anyCard } from './__generated__/index.graphql.ts';

const Wrapper = styled.div`
  position: relative;
  isolation: isolate;
  border-radius: var(--unit);
  border: 1px solid var(--c-nd-150);
  flex-shrink: 0;
  &.auction {
    background-color: var(--c-nd-50);
    border: none;
  }
`;

const ImgWrapper = styled.div`
  position: relative;
  isolation: isolate;
  width: calc(5 * var(--unit));
  flex-shrink: 0;
  .featured & {
    width: 100%;
    max-width: 148px;
  }
`;

const AuctionHeader = styled(Horizontal)`
  padding: var(--half-unit);
  border-radius: var(--unit) var(--unit) 0 0;
  background-color: var(--c-surface-error);
`;

const Relative = styled.div`
  position: relative;
  isolation: isolate;
`;

const Content = styled(Vertical)`
  padding: var(--double-unit);
`;

const PositionedCornerBadges = styled(Vertical).attrs({ gap: 0.5 })`
  align-items: flex-end;
  position: absolute;
  top: var(--unit);
  right: -1px;
  z-index: 1;
`;

const CardWrapper = styled(Horizontal)`
  align-items: center;
  .featured & {
    flex-direction: column;
  }
`;

type Origin = {
  instantBuy: ClickInstantBuy_Flow;
  addToCart: ClickAddToCart_Origin;
};

type Props = {
  anyCard: PlayerDrawerToken_anyCard;
  bestValue?: boolean;
  bestPrice?: boolean;
  highestLevel?: boolean;
  canUseConversionCredit?: boolean;
  origin?: Origin;
  featured?: boolean;
};

export const PlayerDrawerToken = ({
  anyCard,
  bestValue,
  bestPrice = false,
  highestLevel = false,
  canUseConversionCredit = false,
  origin,
  featured,
}: Props) => {
  const auction = useGetAuctionDetails(anyCard.latestEnglishAuction);

  const isBundledAuction = !!(
    anyCard.latestEnglishAuction &&
    isFuture(anyCard.latestEnglishAuction.endDate) &&
    anyCard.latestEnglishAuction?.cards?.length > 1
  );

  return (
    <Wrapper className={classNames({ auction, featured })}>
      {!!auction && (
        <AuctionHeader center>
          <FontAwesomeIcon icon={iconSignalStream} color="var(--c-red-400)" />
          <LabelS color="var(--c-red-400)" bold>
            <TimeLeft time={auction.endDate} />
          </LabelS>
        </AuctionHeader>
      )}
      <Relative>
        <PositionedCornerBadges>
          {!!featured && !!anyCard.livePrimaryOffer && (
            <PriceLockedRibbon
              primaryOffer={anyCard.livePrimaryOffer}
              position="inline"
            />
          )}
          <CornerBadges
            bestValue={bestValue}
            bestPrice={bestPrice}
            highestLevel={highestLevel}
            marketCredit={
              <MarketCreditCornerBadge
                anyCard={anyCard}
                canUseConversionCredit={canUseConversionCredit}
              />
            }
          />
        </PositionedCornerBadges>
        <Content gap={2}>
          <CardWrapper gap={2}>
            <ImgWrapper>
              <TokenImg
                card={anyCard}
                isBundledAuction={isBundledAuction}
                isDesktopLayout={false}
              />
            </ImgWrapper>
            <Details anyCard={anyCard} center={featured} />
          </CardWrapper>
          <Actions anyCard={anyCard} origin={origin} />
          {!!featured && !!anyCard.livePrimaryOffer && (
            <PrimaryOfferStockCounter primaryOffer={anyCard.livePrimaryOffer} />
          )}
        </Content>
      </Relative>
    </Wrapper>
  );
};

PlayerDrawerToken.fragments = {
  anyCard: gql`
    fragment PlayerDrawerToken_anyCard on AnyCardInterface {
      slug
      latestEnglishAuction {
        id
        endDate
        cards: anyCards {
          slug
        }
        ...useGetAuctionDetails_auction
      }
      livePrimaryOffer {
        id
        ...PriceLockedRibbon_primaryOffer
        ...PrimaryOfferStockCounter_primaryOffer
      }
      ...TokenImg_anyCard
      ...PlayerDrawerTokenDetails_anyCard
      ...PlayerDrawerTokenActions_anyCard
      ...useIsPrimaryBuyLive_anyCard
      ...MarketCreditCornerBadge_anyCard
    }
    ${TokenImg.fragments.anyCard}
    ${Details.fragments.anyCard}
    ${Actions.fragments.anyCard}
    ${useGetAuctionDetails.fragments.auction}
    ${MarketCreditCornerBadge.fragments.anyCard}
    ${PriceLockedRibbon.fragments.primaryOffer}
    ${PrimaryOfferStockCounter.fragments.primaryOffer}
  ` as TypedDocumentNode<PlayerDrawerToken_anyCard>,
};
